import type { FetchError, FetchResponse } from 'ofetch'
import type {
  ApiFetcher,
  ApiMethods,
  ApiQueryAndBody,
  DelApiMethod,
  DownloadApiMethod,
  GetApiMethod,
  PostApiMethod,
  UseApiFetchPromise,
  PutApiMethod,
  UseApiFetchBase,
  UseApiFetchError,
  UseApiFetchSuccess,
} from '@/contexts/shared/api/constructor/ApiFetcherConstructor.interfaces'
import type HttpStatusCode from '@/interfaces/HttpStatusCodes.interfaces'

export const extractHeaders = (headers: Headers): Record<string, string | null> => {
  const extracted: Record<string, string | null> = {}
  const keys = headers.keys()

  for (const header of keys) {
    extracted[header] = headers.get(header)
  }

  return extracted
}

export const parseApiResponse = async <T, E>(response: FetchResponse<T>): UseApiFetchPromise<T, E> => {
  const { _data: data, ok, status: code, headers } = response

  const results: UseApiFetchBase<T> = {
    data,
    ok,
    code,
    headers: extractHeaders(headers),
  }

  return ok ? (results as UseApiFetchSuccess<T>) : (results as UseApiFetchError<E>)
}

export const catchApiError = async <E>(error: FetchError): Promise<UseApiFetchError<E>> => {
  return {
    ok: false,
    code: error.statusCode as HttpStatusCode,
    headers: {},
  }
}

const get =
  <Error = unknown>(fetcher: ApiFetcher<Error>): GetApiMethod<Error> =>
  async <T, E = Error>(url: string, query = {}, options = {}): UseApiFetchPromise<T, E> =>
    await fetcher<T, E>(url, {
      method: 'GET',
      ...options,
      query,
    })

const post =
  <Error = unknown>(fetcher: ApiFetcher<Error>): PostApiMethod<Error> =>
  async <T, U, E = Error>(url: string, payload: ApiQueryAndBody<U> = {}, options = {}): UseApiFetchPromise<T, E> =>
    await fetcher<T, E>(url, {
      method: 'POST',
      ...options,
      ...payload,
    })

const put =
  <Error = unknown>(fetcher: ApiFetcher<Error>): PutApiMethod<Error> =>
  async <T, U, E = Error>(url: string, payload: ApiQueryAndBody<U> = {}, options = {}): UseApiFetchPromise<T, E> =>
    await fetcher<T, E>(url, {
      method: 'PUT',
      ...options,
      ...payload,
    })

const del =
  <Error = unknown>(fetcher: ApiFetcher<Error>): DelApiMethod<Error> =>
  async <T, U, E = Error>(url: string, payload: ApiQueryAndBody<U> = {}, options = {}): UseApiFetchPromise<T, E> =>
    await fetcher<T, E>(url, {
      method: 'DELETE',
      ...options,
      ...payload,
    })

const download =
  <Error = unknown>(fetcher: ApiFetcher<Error>): DownloadApiMethod =>
  async <T, E = Error>(url: string, options = {}): Promise<void> => {
    const { ok, data, headers } = await fetcher<T, E>(url, {
      method: 'GET',
      responseType: 'blob',
      ...options,
    })

    if (ok) {
      const { filename } = headers
      const file = window.URL.createObjectURL(data as unknown as Blob)
      const a = document.createElement('a')
      a.href = file
      if (filename) {
        a.download = filename
      }
      document.body.appendChild(a)
      a.click()
      a.remove()
    } else {
      throw new Error('Cannot download file')
    }
  }

export const apiMethods = <Error = unknown>(fetcher: ApiFetcher<Error>): ApiMethods<Error> => ({
  get: get<Error>(fetcher),
  post: post<Error>(fetcher),
  put: put<Error>(fetcher),
  delete: del<Error>(fetcher),
  download: download<Error>(fetcher),
})
